import React, { useState, useRef } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { Bar as BarChart } from 'react-chartjs-2'
import Button from '../../Button/Button'
import { useSelector } from 'react-redux'
import props from '../../../../redux/props'
import { blue } from '../colors'
import { round } from 'lodash'
import getBarTotalPlugin from '../getBarTotalPlugin'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

export type Item = number

type Props = {
  items: Item[]
  enableDownload?: boolean
}

const BarCluster: React.FC<Props> = ({ items, enableDownload = false }) => {
  const t = useSelector((s) => s[props.TRANSLATION])

  // Use a ref to get access to the Chart.js instance
  const chartRef = useRef<any>(null)
  const [image, setImage] = useState<string | null>(null)

  const labels = ['0-25%', '26-50%', '51-75%', '76-100%']

  const data = {
    labels,
    datasets: [
      {
        data: items,
        ...blue,
      },
    ],
  }

  const options = {
    layout: {
      padding: {
        top: 17.5,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || ''
            if (context.parsed) {
              // parsed = { x: number, y: number }
              const { y } = context.parsed
              return `${round(y, 2)}${t.generic.percent}`
            }
            return label
          },
        },
      },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: 'black', // Set the x-axis labels color to black
        },
      },
      y: {
        stacked: true,
        display: false,
        ticks: {
          color: 'black', // Set the y-axis labels color to black
        },
      },
    },
    // Use the Chart.js animation hook to capture the image
    animation: {
      onComplete: () => {
        if (chartRef.current && enableDownload) {
          const chart = chartRef.current
          // 'chart.canvas' is the HTMLCanvasElement
          const canvas = chart.canvas
          setImage(canvas.toDataURL())
        }
      },
    },
  }

  const download = () => {
    if (!image) return
    const a = document.createElement('a')
    a.href = image
    a.download = 'chart.png'
    a.click()
  }

  return (
    <div className="w-full">
      <BarChart ref={chartRef} options={options} data={data} plugins={[getBarTotalPlugin(t.generic.percent)]} />
      {!!image && enableDownload && <Button text={t.generic.download} onClick={download} small className="ml-auto mt-5" />}
    </div>
  )
}

export default BarCluster
